/**
 * The Workspace home strip (Compliance Agent phase B): the person's live
 * cards on the page they land on, and one line to ask compliance without a
 * card. Absent entirely when the agent is off for this person or has
 * nothing to say, so the page looks as it did.
 */
import React, { useCallback, useEffect, useState } from 'react';
import AgentCard from '../Agent/AgentCard.jsx';
import { api } from '../Agent/api.js';

export default function AgentStrip({ initialData = null }) {
  const [me, setMe] = useState(initialData);
  const [asking, setAsking] = useState(false);
  const [q, setQ] = useState('');
  const [msg, setMsg] = useState(null);
  const load = useCallback(async () => {
    const st = await api('/status').catch(() => null);
    if (!st?.enabled) { setMe(null); return; }
    const d = await api('/me').catch(() => null);
    setMe(d?.success ? d : null);
  }, []);
  useEffect(() => { if (!initialData) load(); }, [load, initialData]);
  if (!me) return null;
  const cards = me.cards || [];
  const answered = (me.questions || []).filter((x) => x.status === 'answered').slice(0, 2);
  if (!cards.length && !answered.length && !asking) {
    return (
      <section className="mb-12">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-slate-500">Your agent</h2>
          <button type="button" onClick={() => setAsking(true)} className="text-[12px] text-slate-500 hover:text-slate-800">Ask compliance</button>
        </div>
        <p className="text-[13px] text-slate-500">Nothing needs you right now. The agent will say so here when something is due, late or stalled.</p>
      </section>
    );
  }
  const ask = async () => {
    setMsg(null);
    const d = await api('/questions', { method: 'POST', body: { question: q } });
    if (!d?.success) { setMsg(d?.message || 'Could not send.'); return; }
    setQ(''); setAsking(false); setMsg('Sent to compliance. You will hear back here and by email.');
  };
  return (
    <section className="mb-12" data-agent-strip>
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-slate-500">Your agent</h2>
        <div className="flex items-center gap-3">
          {me.headline && <span className="text-[12px] text-slate-400">{me.headline}</span>}
          <button type="button" onClick={() => setAsking((s) => !s)} className="text-[12px] text-slate-500 hover:text-slate-800">Ask compliance</button>
        </div>
      </div>
      {asking && (
        <div className="mb-3 bg-white rounded-2xl ring-1 ring-slate-200 p-4">
          <textarea value={q} onChange={(e) => setQ(e.target.value)} rows={2} maxLength={4000} placeholder="Ask the compliance team anything about your items. They answer here and by email." className="w-full px-3 py-2 rounded-xl ring-1 ring-slate-200 text-[13px]" />
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={ask} disabled={!q.trim()} className="px-4 py-1.5 rounded-full bg-[#E5601F] text-white text-[13px] font-medium disabled:opacity-50">Ask compliance</button>
            <button type="button" onClick={() => setAsking(false)} className="px-3 py-1.5 rounded-full text-slate-500 text-[13px]">Cancel</button>
          </div>
        </div>
      )}
      {msg && <p className="mb-3 text-[13px] text-slate-600">{msg}</p>}
      {answered.map((x) => (
        <div key={x.id} className="mb-3 bg-white rounded-2xl ring-1 ring-emerald-200 p-4 text-[13px] text-slate-700">
          <p className="text-[11px] uppercase tracking-wide text-emerald-700 mb-1">Compliance answered{x.object_label ? ` · ${x.object_label}` : ''}</p>
          <p className="text-slate-500">You asked: {x.question}</p>
          <p className="mt-1 text-slate-900">{x.answer}</p>
          <p className="mt-1 text-slate-400">{x.answered_by_name || 'Compliance'}{x.standing ? ' · standing guidance' : ''}</p>
        </div>
      ))}
      <div className="space-y-3">
        {cards.map((c) => <AgentCard key={c.id} card={c} compact brand={me.brand || null} onDone={() => load()} />)}
      </div>
    </section>
  );
}
